import React from "react";

import { Card, CardContentGroup, CheckboxRound, Flex, Image, Radio, Skeleton } from "components";
import type { CheckboxChangeEvent, TagColour } from "components";

export interface SelectableImageCardProps {
  image: {
    imageSrc: string;
    imageAlt: string;
    imageSize?: number;
  };
  title: string;
  description?: string;
  largeDescription?: boolean;
  tag?: {
    text: string;
    colour: TagColour;
    tooltip?: string;
  };
  selectionMode?: "single" | "multiple";
  disabled?: boolean;
  loading?: boolean;
  onChange: (e: Partial<CheckboxChangeEvent>) => void;
  checked: boolean;
}

export const SelectableImageCard: React.FC<SelectableImageCardProps> = ({
  image,
  title,
  description,
  largeDescription,
  tag,
  selectionMode,
  loading,
  disabled,
  checked,
  onChange,
}) => {
  const { imageSrc, imageAlt, imageSize = 48 } = image;
  const tagArray = tag ? [tag] : undefined;
  const showRadioButton = selectionMode === "single";
  const showCheckbox = selectionMode === "multiple";

  const handleCardClick = () => {
    if (!disabled) {
      onChange({ target: { checked: !checked } });
    }
  };

  if (loading) {
    return (
      <Card className="padding-125 width-100 margin-top-100">
        <Flex gap="28px" align="center">
          <div className="skeleton-containers icon">
            <Skeleton type="block" height="48px" width="48px" className="skeleton-rectangular" />
          </div>

          <Flex className="width-100" vertical gap="8px">
            <div className="skeleton-containers tag margin-bottom-50">
              <Skeleton type="title" width="100px" className="skeleton-rectangular" />
            </div>

            <div>
              <div className="skeleton-containers form-title margin-bottom-25">
                <Skeleton type="title" width="180px" className="skeleton-rectangular" />
              </div>
              <div className="skeleton-containers form-description margin-bottom-25">
                <Skeleton type="title" width="240px" className="skeleton-rectangular" />
              </div>
            </div>
          </Flex>

          <div className="skeleton-containers progress">
            <Skeleton type="block" height="18px" width="40px" className="skeleton-rectangular" />
          </div>
          <div className="skeleton-containers edit">
            <Skeleton type="block" width="24px" height="18px" className="skeleton-rectangular" />
          </div>
        </Flex>
      </Card>
    );
  }

  return (
    <Card
      className={`padding-top-75 padding-bottom-75 padding-right-100 padding-left-100 width-100 margin-top-100 selected-image-card ${
        checked ? "selected" : ""
      } ${disabled ? "disabled" : ""}`}
      onClick={handleCardClick}
    >
      <Flex align="center" gap="12px">
        {/* Image */}
        <Flex align="center" justify="center" className="image-container">
          <Image
            src={imageSrc || "https://klimpublic.s3.eu-central-1.amazonaws.com/footprintingImagePlaceholder.png"}
            alt={imageAlt}
            width={imageSize}
            height={imageSize}
            style={{ width: imageSize, height: imageSize, objectFit: "cover" }}
            className="selected-image-card-image"
            key={""}
            preview={false}
            images={[]}
          />
        </Flex>

        {/* Content Column */}
        <CardContentGroup grow title={title} description={description} largeDescription={largeDescription} tag={tagArray} tagPosition="inline" />

        {/* Controls */}
        <div className="center">
          <Flex gap={12} align="center" justify="flex-end">
            {showRadioButton && <Radio disabled={disabled} onChange={onChange} checked={checked} />}
            {showCheckbox && <CheckboxRound disabled={disabled} onChange={onChange} checked={checked} />}
          </Flex>
        </div>
      </Flex>
    </Card>
  );
};

export default SelectableImageCard;
